import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppCard } from './ui/AppCard';
import { Button } from './ui/Button';
import { Toggle } from './ui/Toggle';
import { readConsent, writeConsent } from '../lib/cookieConsent';

// Mounted once at the app root (see App.tsx) so it's reachable from every
// screen, public or logged-in. Opens itself on first visit (no stored
// consent); afterwards it's reopened via reopenCookieBanner() (Settings has
// a "Cookie settings" row for this).
let externalOpen: (() => void) | null = null;
export function reopenCookieBanner() {
  externalOpen?.();
}

export function CookieConsentBanner() {
  const [open, setOpen] = useState(false);
  const [managing, setManaging] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!readConsent()) setOpen(true);
    externalOpen = () => {
      const existing = readConsent();
      if (existing) {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
        setManaging(true);
      }
      setOpen(true);
    };
    return () => {
      externalOpen = null;
    };
  }, []);

  if (!open) return null;

  function decide(categories: { analytics: boolean; marketing: boolean }) {
    writeConsent(categories);
    setOpen(false);
    setManaging(false);
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4 sm:pb-6">
      <AppCard className="w-full max-w-lg shadow-2xl">
        <p className="text-[14px] text-black/70 dark:text-white/70 leading-relaxed">
          zmist uses essential local storage to keep you signed in and remember your settings. See our{' '}
          <Link to="/cookies" className="text-blue-600 dark:text-blue-400 hover:underline">
            Cookie Policy
          </Link>{' '}
          for details - nothing else is set without your say-so.
        </p>

        {!managing ? (
          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="bordered" onClick={() => setManaging(true)}>
              Manage
            </Button>
            <Button variant="secondary" onClick={() => decide({ analytics: false, marketing: false })}>
              Essential only
            </Button>
            <Button variant="primary" className="ml-auto" onClick={() => decide({ analytics: true, marketing: true })}>
              Accept all
            </Button>
          </div>
        ) : (
          <div className="mt-4 flex flex-col divide-y divide-black/[0.06] dark:divide-white/[0.08]">
            <div className="py-2 flex items-center justify-between gap-4">
              <div>
                <span className="text-[15px] block">Essential</span>
                <span className="text-[13px] text-black/50 dark:text-white/50">
                  Sign-in state and your saved preferences. zmist doesn't work without these.
                </span>
              </div>
              <span className="text-[13px] font-medium text-black/40 dark:text-white/40 shrink-0">Always on</span>
            </div>
            <div className="py-2">
              <Toggle label="Analytics" checked={analytics} onChange={setAnalytics} />
              <span className="text-[13px] text-black/50 dark:text-white/50 block -mt-1">
                Would help us see how zmist is used, in aggregate. Not currently in use.
              </span>
            </div>
            <div className="py-2">
              <Toggle label="Marketing" checked={marketing} onChange={setMarketing} />
              <span className="text-[13px] text-black/50 dark:text-white/50 block -mt-1">
                Would help us measure and target zmist's own ads. Not currently in use.
              </span>
            </div>
            <Button variant="primary" full className="mt-4" onClick={() => decide({ analytics, marketing })}>
              Save preferences
            </Button>
          </div>
        )}
      </AppCard>
    </div>
  );
}
